import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useApp } from '../context/AppContext';
import { submitLead } from '../api';
import { isValidPhone, PHONE_ERROR } from '../lib/phone';
import { PhoneField } from './PhoneField';
import { trackLeadConversion } from '../lib/analytics';

// Simple global event bus so any button can open the enquiry modal.
export const openEnquiry = (projectName = '') => {
  window.dispatchEvent(new CustomEvent('gobra:enquire', { detail: { projectName } }));
};

const EnquiryModal = () => {
  const { t, settings } = useApp();
  const [open, setOpen] = useState(false);
  const [project, setProject] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', mobile: '', email: '', note: '' });

  useEffect(() => {
    const handler = (e) => {
      setProject(e.detail?.projectName || '');
      setSent(false);
      setError('');
      setForm({ name: '', mobile: '', email: '', note: e.detail?.projectName ? `I am interested in ${e.detail.projectName}.` : '' });
      setOpen(true);
    };
    window.addEventListener('gobra:enquire', handler);
    return () => window.removeEventListener('gobra:enquire', handler);
  }, []);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!isValidPhone(form.mobile)) { setError(PHONE_ERROR); return; }
    setSubmitting(true);
    setError('');
    try {
      await submitLead({ ...form, project });
      trackLeadConversion();
      setSent(true);
    } catch (err) {
      setError(t('enquiry_error') || 'Sorry, we could not send your enquiry. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  }, [form, project, t]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md bg-gobra-cream border-none p-0 overflow-hidden">
        <div className="gobra-gradient px-6 py-5">
          <DialogHeader>
            <DialogTitle className="font-title text-2xl text-white">
              {sent ? t('thanks_title') : t('enquire_now')}
            </DialogTitle>
          </DialogHeader>
          {project && !sent && <p className="text-white/80 text-sm mt-1">{project}</p>}
        </div>
        {sent ? (
          <div className="px-6 py-8 text-center">
            <p className="text-gobra-slate">{t('thanks_msg')}</p>
            <Button onClick={() => setOpen(false)} className="mt-6 bg-gobra-green hover:bg-gobra-green-dark text-white">
              {t('close')}
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-6 space-y-4">
            <Input required placeholder={t('name')} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white" />
            <PhoneField value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })}
              testid="enquiry-mobile" placeholder={t('mobile')} />
            <Input required type="email" placeholder={t('email')} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white" />
            <Textarea placeholder={t('note')} value={form.note} rows={3}
              onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-white" />
            {error && <p data-testid="enquiry-error" className="text-sm text-center text-destructive">{error}</p>}
            <Button type="submit" disabled={submitting} className="w-full bg-gobra-terracotta hover:bg-gobra-green text-white rounded-full py-6 text-base">
              {submitting ? '...' : t('send_enquiry')}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              {settings?.phone} — {settings?.email}
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnquiryModal;
